import { useState, useRef, useEffect, useCallback } from 'react';
import { useStudent } from '../../../peoples/students/hooks/useStudent';

/** Number of records fetched per lazy chunk */
const LAZY_PAGE_SIZE = 100;

const FILTER_STATE_KEY = "assign_marks_filter_state";

export const useAssignMarksFilters = (isSuperAdmin: boolean) => {
  const getStoredFilterState = () => {
    const storedState = sessionStorage.getItem(FILTER_STATE_KEY);
    if (storedState) {
      try {
        return JSON.parse(storedState);
      } catch (e) {
        console.error("Error parsing stored filter state:", e);
      }
    }
    return null;
  };

  const storedState = getStoredFilterState();

  const [selectedSession, setSelectedSession] = useState<string>(storedState?.selectedSession || "");
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(storedState?.selectedCourseId || null);
  const [selectedLevel, setSelectedLevel] = useState<string>(storedState?.selectedLevel || "");
  const [selectedCenterId, setSelectedCenterId] = useState<string>(storedState?.selectedCenterId || "");
  const [recordsPerPage, setRecordsPerPage] = useState(storedState?.recordsPerPage || 10);
  const [currentPage, setCurrentPage] = useState(storedState?.currentPage || 1);
  const [searchText, setSearchText] = useState("");

  // Store filter state in session storage whenever it changes
  useEffect(() => {
    const filterState = {
      selectedSession,
      selectedCourseId,
      selectedLevel,
      selectedCenterId,
      recordsPerPage,
      currentPage,
    };
    sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify(filterState));
  }, [
    selectedSession,
    selectedCourseId,
    selectedLevel,
    selectedCenterId,
    recordsPerPage,
    currentPage,
  ]);

  // Lazy Loading States
  const [isAllMode, setIsAllMode] = useState(false);
  const [lazyPage, setLazyPage] = useState(1);
  const [accumulatedStudents, setAccumulatedStudents] = useState<any[]>([]);
  const [lazyHasMore, setLazyHasMore] = useState(true);

  const lastProcessedPage = useRef(0);
  const pageRequestedRef = useRef(false);

  // RTK Query
  const { getStudentState, paginationData } = useStudent({
    page: isAllMode ? lazyPage : currentPage,
    size: isAllMode ? LAZY_PAGE_SIZE : recordsPerPage,
    search: searchText,
    course_id: selectedCourseId || undefined,
    stage_id: selectedLevel ? Number(selectedLevel) : undefined,
    session_id: selectedSession ? Number(selectedSession) : undefined,
    ...(isSuperAdmin && selectedCenterId
      ? { center_id: Number(selectedCenterId) }
      : {}),
    status: "active",
    marks: true, // Make sure to request marks with student data
  });

  const { students: currentPageStudents, isLoading: isLoadingStudents, isFetching: isFetchingStudents } = getStudentState();

  // Reset lazy state helper
  const resetLazyState = useCallback(() => {
    setAccumulatedStudents([]);
    setLazyPage(1);
    lastProcessedPage.current = 0;
    pageRequestedRef.current = false;
    setLazyHasMore(true);
  }, []);

  // Reset accumulation when search or filters change
  useEffect(() => {
    resetLazyState();
  }, [searchText, selectedCourseId, selectedLevel, selectedSession, selectedCenterId, isAllMode, resetLazyState]);

  // Accumulation effect
  useEffect(() => {
    if (currentPageStudents && currentPageStudents.length >= 0) {
      if (isAllMode) {
        if (isFetchingStudents) return;
        if (lazyPage === lastProcessedPage.current) return;

        // No data -> end of results
        if (currentPageStudents.length === 0) {
          setLazyHasMore(false);
          pageRequestedRef.current = false;
          return;
        }

        setLazyHasMore(currentPageStudents.length === LAZY_PAGE_SIZE);
        lastProcessedPage.current = lazyPage;
        pageRequestedRef.current = false;

        setAccumulatedStudents((prev) => {
          if (lazyPage === 1) return currentPageStudents;
          const newUnique = currentPageStudents.filter(
            (newS: any) => !prev.some((p: any) => p.id === newS.id)
          );
          return [...prev, ...newUnique];
        });
        
        const total = paginationData.totalItems || 0;
        const fetchedSoFar = lazyPage * LAZY_PAGE_SIZE;
        if (currentPageStudents.length < LAZY_PAGE_SIZE || fetchedSoFar >= total) {
          setLazyHasMore(false);
        }
      }
    }
  }, [currentPageStudents, isAllMode, lazyPage, isFetchingStudents, paginationData.totalItems]);

  const loadNextLazyPage = useCallback(() => {
    if (!isAllMode || !lazyHasMore) return;
    if (pageRequestedRef.current) return;
    pageRequestedRef.current = true;
    setLazyPage(prev => prev + 1);
  }, [isAllMode, lazyHasMore]);

  const handlePaginationChange = (page: number, pageSize?: number) => {
    if (pageSize !== undefined) {
      const total = paginationData.totalItems || 0;
      const isSelectingAll = total > 0 && pageSize >= total;

      if (isSelectingAll) {
        setIsAllMode(true);
        resetLazyState();
        setRecordsPerPage(pageSize);
        return;
      }

      if (isAllMode) {
        setIsAllMode(false);
        resetLazyState();
      }
      setRecordsPerPage(pageSize);
    }
    setCurrentPage(page);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
    if (isAllMode) resetLazyState();
  };

  return {
    selectedSession,
    setSelectedSession,
    selectedCourseId,
    setSelectedCourseId,
    selectedLevel,
    setSelectedLevel,
    selectedCenterId,
    setSelectedCenterId,
    recordsPerPage,
    currentPage,
    setCurrentPage,
    searchText,
    setSearchText,
    isAllMode,
    accumulatedStudents,
    lazyHasMore,
    currentPageStudents,
    isLoadingStudents,
    isFetchingStudents,
    paginationData,
    pageRequestedRef,
    loadNextLazyPage,
    handleSearch,
    handlePaginationChange,
  };
};
